import { ASPECT_RATIO_TOLERANCE } from '@/app/constants';
import type { FormImagePrompt, FormImagePromptWithSize } from '@/app/types';
import { getImageRatio } from './get-image-ratio';
import { checkSourceImageSize, getImageSize } from './image-utils';

export const getFirstRatioMismatchIndex = (images: (FormImagePrompt | null)[]): number => {
  const firstPrompt = images[0]?.prompt;
  if (!firstPrompt || !checkSourceImageSize(firstPrompt)) return -1;

  const baseRatio = getImageRatio(firstPrompt);
  for (let i = 1; i < images.length; i++) {
    const curPrompt = images[i]?.prompt;
    if (!curPrompt || !checkSourceImageSize(curPrompt)) continue;
    const ratio = getImageRatio(curPrompt);
    const exceedsTolerance = ratio / baseRatio > ASPECT_RATIO_TOLERANCE || baseRatio / ratio > ASPECT_RATIO_TOLERANCE;
    if (exceedsTolerance) return i;
  }
  return -1;
};

export const ensureSingleImageSize = async (prompt: FormImagePrompt): Promise<FormImagePromptWithSize | null> => {
  if (!prompt?.prompt) return null;
  if (checkSourceImageSize(prompt.prompt)) return prompt as FormImagePromptWithSize;

  try {
    const { width, height } = await getImageSize(prompt.prompt.sourceImagePreview);
    return {
      ...prompt,
      prompt: {
        ...prompt.prompt,
        sourceImageWidth: width,
        sourceImageHeight: height,
      },
    };
  } catch {
    return null;
  }
};

export const getHomeCropCancelImageIds = ({
  mode,
  imagePrompts,
  baselineImageIds,
}: {
  mode: 'upload' | 'mode-switch';
  imagePrompts: (FormImagePrompt | null)[];
  baselineImageIds: string[];
}): string[] => {
  const validImagePrompts = imagePrompts.filter((prompt): prompt is FormImagePrompt => Boolean(prompt));

  if (mode === 'mode-switch') {
    return validImagePrompts.slice(1).map((prompt) => prompt.id);
  }

  const baselineIds = new Set(baselineImageIds);
  return validImagePrompts.filter((prompt) => !baselineIds.has(prompt.id)).map((prompt) => prompt.id);
};
